import { useSlot, useStyled } from "use-styled";
import { Pressable, Text } from "react-native";
import { ComponentType, Fragment } from "react";
import { SvgProps } from "react-native-svg";

const ButtonRoot = useStyled(Pressable, {
  base: {
    className: "rounded-full items-center align-center justify-center border-2",
    accessibilityRole: "button",
    style: {},
    props: true,
  },
  variants: {
    intent: {
      primary: {
        className:
          "bg-teachLoop-accentPrimary border-teachLoop-accentPrimary hover:border-teal-700 hover:bg-teal-700 active:border-teal-800 active:bg-teal-800",
      },
      secondary: {
        className:
          "bg-teachLoop-upsell border-teachLoop-upsell hover:border-orange-600 hover:bg-orange-600 active:border-orange-700 active:bg-orange-700",
      },
      danger: {
        className:
          "bg-red-500 border-red-500 hover:border-red-600 hover:bg-red-600 active:border-red-700 active:bg-red-700",
      },
      textPrimary: {
        className:
          "bg-transparent border-teachLoop-textPrimary hover:bg-teachLoop-textPrimary active:bg-transparent",
      },
      light: {
        className: "bg-teachLoop-creamLight border-teachLoop-creamLight hover:bg-teachLoop-creamLight active:bg-transparent",
      }
    },
    size: {
      sm: { className: "px-3 py-2" },
      md: { className: "px-4 py-3" },
    },
    outline: {
      true: {
        className: "bg-transparent hover:bg-transparent active:bg-transparent",
      },
    },
    disabled: {
      true: { className: "opacity-50" },
    },
    borderLess: {
      true: {
        className:
          "bg-transparent border-transparent hover:bg-transparent hover:border-transparent active:bg-transparent active:border-transparent",
      },
    },
    fullWidth: {
      false: { className: "w-fit" },
    },
    icon: {
      true: { className: "gap-2 flex-row" },
    },
  },
  defaultVariants: {
    intent: "primary",
    size: "md",
    outline: false,
    disabled: false,
    fullWidth: false,
  },
  compoundVariants: [
    {
      disabled: true,
      props: {
        disabled: true,
      },
    },
  ],
});

const ButtonText = useStyled(Text, {
  base: {
    className: "font-geologica-medium font-medium",
  },
  variants: {
    intent: {
      default: { className: "text-teachLoop-creamLight" },
      primary: {
        className: "text-teal-600 hover:text-teal-700 active:text-teal-800",
      },
      secondary: {
        className:
          "text-orange-500 hover:text-orange-600 active:text-orange-700",
      },
      danger: {
        className: "text-red-500 hover:text-red-600 active:text-red-700",
      },
      textPrimary: { className: "text-teachLoop-textPrimary hover:text-teachLoop-textPrimary active:text-teachLoop-textPrimary" },
      light: { className: "text-teachLoop-creamLight hover:text-teachLoop-creamLight active:text-teachLoop-creamLight" },
    },
    size: {
      sm: { className: "text-base leading-6" },
      md: { className: "text-lg leading-6" },
    },
  },
  defaultVariants: {
    size: "md",
    intent: "default",
  },
});

function ButtonIconWrapper({
  as,
  ...props
}: SvgProps & {
  className?: string;
  pathClassName?: string;
  as?: ComponentType<SvgProps & { className?: string; pathClassName?: string }>;
}) {
  const AsIcon = as ?? Fragment;
  return <AsIcon {...props} />;
}

const ButtonIcon = useStyled(ButtonIconWrapper, {
  base: {
    className: "size-4 justify-center items-center",
  },
  variants: {
    intent: {
      default: { pathClassName: "stroke-white fill-white" },
      primary: { pathClassName: "stroke-teal-600 fill-teal-600" },
      secondary: { pathClassName: "stroke-orange-500 fill-orange-500" },
      danger: { pathClassName: "stroke-red-500 fill-red-500" },
      textPrimary: { pathClassName: "stroke-teachLoop-textPrimary fill-teachLoop-textPrimary" },
      light: { pathClassName: "stroke-teachLoop-creamLight fill-teachLoop-creamLight" },
    },
  },
  defaultVariants: {
    intent: "default",
  },
});

export const Button = useSlot(ButtonRoot, {
  Text: ButtonText,
  Icon: ButtonIcon,
});
